import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] bg-grid flex items-center justify-center px-4 relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[500px] h-[400px] rounded-full bg-sky-700/15 blur-[140px]" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="glass rounded-2xl p-8 sm:p-10 w-full max-w-md relative"
      >
        <div className="flex flex-col items-center text-center">
          <Logo size={52} />
          <h1 className="mt-5 font-display font-extrabold text-2xl text-white">
            Portal Admin <span className="text-sky-400">INDORAWDAH</span>
          </h1>
          <p className="mt-2 text-sm text-slate-400">Masuk untuk mengelola pengunjung & pesanan permit.</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="block text-xs font-medium text-slate-300 mb-2">Email Admin</label>
            <input
              id="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@rawdahpass.id"
              data-testid="admin-login-email-input"
              className="w-full rounded-xl bg-[#0A0F1D] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-400/60 focus:ring-1 focus:ring-sky-400/40 transition-colors duration-200"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-xs font-medium text-slate-300 mb-2">Password</label>
            <input
              id="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="admin-login-password-input"
              className="w-full rounded-xl bg-[#0A0F1D] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-400/60 focus:ring-1 focus:ring-sky-400/40 transition-colors duration-200"
            />
          </div>
          {error && (
            <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-white bg-gradient-to-br from-sky-500 to-sky-700 hover:from-sky-400 hover:to-sky-600 transition-all duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 size={17} className="animate-spin" /> : <Lock size={16} />}
            {loading ? "Memeriksa..." : "Masuk Portal Admin"}
          </button>
        </form>

        <Link to="/" data-testid="admin-login-back-link" className="mt-6 block text-center text-sm text-slate-400 hover:text-white transition-colors duration-200">
          ← Kembali ke Beranda
        </Link>
      </motion.div>
    </div>
  );
}
